import { HEADLINE_MAX, MAX_CATEGORY_BLOCKS, NOTE_MAX } from '@src/components/sco/Advisors/data/becomeAdvisor'
import { categoryKeysToAdviseTopics } from '@src/components/sco/Advisors/utils/categoryKeys'
import { clipAtWord } from '@src/components/sco/Advisors/utils/clipAtWord'
import { ScoAdvisorProfile, ScoUserExperience, ScoUserSkill } from '@src/graphql-types'

export const MAX_BRANDS = 3
export const MAX_EXPERIENCES = 6
export const MAX_SKILLS = 12

export const PLATFORM_COMPENSATION_KEYS = ['cash', 'equity', 'profit-share', 'none'] as const

export type ApplyExperience = {
  clientKey: string
  userExperienceId: string | null
  organizationName: string
  experienceTypeKey: string
  positionDescription: string
}

export type AdvisorApplicationValues = {
  categories: string[]
  skills: string[]
  headline: string
  experiences: ApplyExperience[]
  note: string
  compensationKeys: string[]
  engagementStyleKeys: string[]
  warmIntroKeys: string[]
}

export type ApplyUserSkill = Pick<
  ScoUserSkill,
  'userSkillId' | 'skillId' | 'title' | 'userRoleKeys' | 'compensationTypeKeys'
> & {
  skill?: { title?: string | null } | null
  deletedAt?: string | Date | null
}

const PLATFORM_COMP_SET = new Set<string>(PLATFORM_COMPENSATION_KEYS)

export const cleanStringList = (values?: Array<string | null> | null): string[] =>
  (values ?? []).map(value => (value ?? '').trim()).filter(Boolean)

export const filterBooleanKeys = (keys?: Array<string | null> | null): string[] =>
  (keys ?? []).map(key => (key ?? '').trim()).filter(Boolean)

export const titleKey = (title: string): string => title.trim().toLowerCase()

export const mergeAdvisorRoleKeys = (existing?: Array<string | null> | null): string[] => {
  const keys = filterBooleanKeys(existing)
  if (!keys.includes('advisor')) {
    keys.push('advisor')
  }
  return keys
}

export const platformCompensationKeys = (keys?: Array<string | null> | null): string[] => [
  ...new Set(filterBooleanKeys(keys).filter(key => PLATFORM_COMP_SET.has(key))),
]

export const skillTitleFromUserSkill = (skill: ApplyUserSkill): string =>
  (skill.skill?.title || skill.title || '').trim()

export const categoryTitleSet = (categories: string[]): Set<string> =>
  new Set(categories.map(titleKey).filter(Boolean))

/** Unique titles, first spelling wins. */
export const uniqueTitles = (titles: string[]): string[] => {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of titles) {
    const title = raw.trim()
    if (!title) {
      continue
    }
    const key = titleKey(title)
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    out.push(title)
  }
  return out
}

/** Keep the current value when it already has text. */
export const fillBlank = (current: string, incoming?: string | null): string =>
  current.trim() ? current : String(incoming || '').trim()

export type ExtractedApplyExperience = {
  organizationName?: string | null
  experienceTypeKey?: string | null
  positionDescription?: string | null
  userExperienceId?: string | null
}

export type ExtractedApplicationFields = {
  headline?: string | null
  categories?: string[] | null
  skills?: string[] | null
  experiences?: Array<ExtractedApplyExperience | null> | null
}

/** Merge extracted apply answers onto existing form values without replacing filled fields. */
export const mergeExtractedApplicationValues = (
  prev: AdvisorApplicationValues,
  extracted: ExtractedApplicationFields
): AdvisorApplicationValues => {
  const incomingCategories = (extracted.categories || []).filter((value): value is string => !!value)
  const incomingSkills = (extracted.skills || []).filter((value): value is string => !!value)
  const categories = uniqueTitles([...prev.categories, ...incomingCategories]).slice(0, MAX_CATEGORY_BLOCKS)
  const blocked = categoryTitleSet(categories)
  const skills = uniqueTitles([...prev.skills, ...incomingSkills])
    .filter(title => !blocked.has(titleKey(title)))
    .slice(0, MAX_SKILLS)
  const headline = fillBlank(prev.headline, clipAtWord(extracted.headline, HEADLINE_MAX))

  return {
    ...prev,
    categories,
    skills,
    headline,
    experiences: mergeExtractedExperiences(prev.experiences, extracted.experiences),
  }
}

export const mergeExtractedExperiences = (
  existing: ApplyExperience[],
  extracted?: Array<ExtractedApplyExperience | null> | null
): ApplyExperience[] => {
  const next = existing.map(row => ({ ...row }))
  const indexByOrg = new Map<string, number>()
  next.forEach((row, index) => {
    const key = titleKey(row.organizationName)
    if (key && !indexByOrg.has(key)) {
      indexByOrg.set(key, index)
    }
  })

  for (const row of extracted || []) {
    const organizationName = row?.organizationName?.trim()
    if (!organizationName) {
      continue
    }
    const key = titleKey(organizationName)
    const index = indexByOrg.get(key)
    if (index != null) {
      const current = next[index]
      next[index] = {
        ...current,
        userExperienceId: current.userExperienceId || row.userExperienceId || null,
        clientKey: current.userExperienceId || row.userExperienceId || current.clientKey,
        positionDescription: current.positionDescription.trim() || row.positionDescription?.trim() || '',
      }
      continue
    }
    if (next.length >= MAX_EXPERIENCES) {
      continue
    }
    const userExperienceId = row.userExperienceId || null
    next.push({
      clientKey: userExperienceId || `extract-${next.length}`,
      userExperienceId,
      organizationName,
      experienceTypeKey: row.experienceTypeKey?.trim() || 'job',
      positionDescription: row.positionDescription?.trim() || '',
    })
    indexByOrg.set(key, next.length - 1)
  }

  return next
}

export const managedSkillTitles = (categories: string[], pickerSkills: string[]): string[] =>
  uniqueTitles([...categories, ...pickerSkills])

export const pickerSkillsFromUserSkills = (
  userSkills: ApplyUserSkill[] | null | undefined,
  categoryTitles: string[]
): string[] => {
  const categories = categoryTitleSet(categoryTitles)
  return uniqueTitles(
    (userSkills || [])
      .filter(skill => !skill.deletedAt)
      .map(skillTitleFromUserSkill)
      .filter(title => title && !categories.has(titleKey(title)))
  )
}

export const matchUserSkillByTitle = (
  userSkills: ApplyUserSkill[] | null | undefined,
  title: string
): ApplyUserSkill | undefined => {
  const key = titleKey(title)
  return (userSkills || []).find(
    skill => !skill.deletedAt && titleKey(skillTitleFromUserSkill(skill)) === key
  )
}

export const emptyExperience = (index = 0): ApplyExperience => ({
  clientKey: `new-${index}-${Date.now()}`,
  userExperienceId: null,
  organizationName: '',
  experienceTypeKey: 'job',
  positionDescription: '',
})

export const experienceFromUserExperience = (
  row: Pick<
    ScoUserExperience,
    'userExperienceId' | 'organizationName' | 'experienceTypeKey' | 'positionDescription'
  >,
  index: number
): ApplyExperience => ({
  clientKey: row.userExperienceId || `exp-${index}`,
  userExperienceId: row.userExperienceId || null,
  organizationName: row.organizationName || '',
  experienceTypeKey: row.experienceTypeKey || 'job',
  positionDescription: row.positionDescription || '',
})

export const experiencesFromUserAndProfile = ({
  userExperiences,
  notableBrands,
}: {
  userExperiences?: Array<Pick<
    ScoUserExperience,
    'userExperienceId' | 'organizationName' | 'experienceTypeKey' | 'positionDescription'
  > | null> | null
  notableBrands?: Array<string | null> | null
}): ApplyExperience[] => {
  const fromUser = (userExperiences || [])
    .filter((row): row is NonNullable<typeof row> => !!row)
    .map((row, index) => experienceFromUserExperience(row, index))
  if (fromUser.length) {
    return fromUser
  }
  return cleanStringList(notableBrands).map((organizationName, index) => ({
    clientKey: `brand-${index}`,
    userExperienceId: null,
    organizationName,
    experienceTypeKey: 'job',
    positionDescription: '',
  }))
}

const compensationFromManagedSkills = (
  userSkills: ApplyUserSkill[] | null | undefined,
  managedTitles: string[]
): string[] => {
  const managed = new Set(managedTitles.map(titleKey))
  const keys: string[] = []
  for (const skill of userSkills || []) {
    if (skill.deletedAt) {
      continue
    }
    if (!managed.has(titleKey(skillTitleFromUserSkill(skill)))) {
      continue
    }
    keys.push(...platformCompensationKeys(skill.compensationTypeKeys))
  }
  return [...new Set(keys)]
}

export const valuesFromUserAndProfile = ({
  profile,
  userSkills,
  userExperiences,
  shortBio,
}: {
  profile?: ScoAdvisorProfile | null
  userSkills?: ApplyUserSkill[] | null
  userExperiences?: Array<Pick<
    ScoUserExperience,
    'userExperienceId' | 'organizationName' | 'experienceTypeKey' | 'positionDescription'
  > | null> | null
  shortBio?: string | null
}): AdvisorApplicationValues => {
  const fromKeys = categoryKeysToAdviseTopics(cleanStringList(profile?.categoryKeys))
  const fromTitles = categoryKeysToAdviseTopics(
    (profile?.categories ?? []).map(category => category?.key).filter((key): key is string => !!key)
  )
  const categories = fromKeys.length ? fromKeys : fromTitles
  const fromUserSkills = pickerSkillsFromUserSkills(userSkills, categories)
  const fromProfileTitles = cleanStringList(profile?.skillTagTitles).filter(
    title => !categoryTitleSet(categories).has(titleKey(title))
  )
  const skills = fromUserSkills.length ? fromUserSkills : fromProfileTitles
  const managed = managedSkillTitles(categories, skills)
  const fromSkillsComp = compensationFromManagedSkills(userSkills, managed)
  const fromProfileComp = platformCompensationKeys(profile?.preferredCompensationTypeKeys)

  return {
    categories,
    skills,
    headline: clipAtWord(shortBio || profile?.headline, HEADLINE_MAX),
    experiences: experiencesFromUserAndProfile({
      userExperiences,
      notableBrands: profile?.notableBrands,
    }),
    note: (profile?.advisingNote || '').slice(0, NOTE_MAX),
    compensationKeys: fromSkillsComp.length
      ? fromSkillsComp
      : fromProfileComp.length
        ? fromProfileComp
        : ['equity'],
    engagementStyleKeys: cleanStringList(profile?.engagementStyleKeys),
    warmIntroKeys: cleanStringList(profile?.warmIntroKeys),
  }
}

/** Profile-only hydrate for stories / pending preview without a UserSkills query. */
export const valuesFromProfile = (profile: ScoAdvisorProfile): AdvisorApplicationValues =>
  valuesFromUserAndProfile({ profile })

export const titlesToDelete = (previousManaged: string[], nextManaged: string[]): string[] => {
  const next = new Set(nextManaged.map(titleKey))
  return uniqueTitles(previousManaged.filter(title => !next.has(titleKey(title))))
}

export const derivedNotableBrands = (experiences: ApplyExperience[]): string[] =>
  uniqueTitles(experiences.map(experience => experience.organizationName)).slice(0, MAX_BRANDS)

export const derivedSkillTagTitles = (categories: string[], skills: string[]): string[] =>
  managedSkillTitles(categories, skills).slice(0, MAX_SKILLS)
